import { Clear } from "@mui/icons-material";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps, useHistory } from "react-router";
import { Filters, SortingRule } from "react-table";
import { NetworkRequest } from "../../../common/models";
import { DataRequestFilter, DataRequestShowType } from "../../../common/service/server/Types";
import {
  LoadingPageWrapper,
  StandardPageWrapper,
} from "../../components/layout/StandardPageWrapper";
import { NewRequestDialog } from "../../components/dialog/NewRequestDialog";
import { PageHeader } from "../../components/layout/header/Header";
import {
  useOrganization,
  usePrimaryOrganizationId,
  useRequestSummaries,
  usePrivacyEmailConversations,
} from "../../hooks";
import { RouteHelpers, Routes } from "../../root/routes";
import { RequestsTable } from "./RequestsTable";
import { ScheduleTrainingDialog } from "../../components/dialog/ScheduleTrainingDialog";
import { useQueryModal } from "../../hooks/modalHooks";
import useStatefulPagination from "../../../common/hooks/useStatefulPagination";
import { useRequestBatches } from "../../hooks/useRequests";
import {
  DataSubjectRequestSummaryDto,
  OrganizationDataSubjectRequestBatchDto,
} from "../../../common/service/server/dto/DataSubjectRequestDto";
import { DueDate } from "../../components/DueDateCard";
import { AUTOMATING_TAB_LABEL, fmtLongRequestStep } from "../../formatters/request";
import axios from "axios";
import { canUsePrivacyEmailInbox } from "../../util/privacyEmailSetup";
import { ApiRouteHelpers } from "../../../common/root/apiRoutes";
import { Button as AquiloButton, Modal, TabsWithUnderline, useSnackBar } from "@truevault/aquilo";
import { useRequestCSVDownloader } from "../../../common/hooks/useRequestCSVDownloader";
import { useRowSelectionManager } from "../../../common/hooks/useRowSelectionManager";
import { ReconnectGANudgeDialog } from "../../components/dialog/ReconnectGANudgeDialog";
import { useAuthorizations } from "../../hooks/useAuthorizations";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { PlusIcon } from "@heroicons/react/24/solid";
import { DocumentArrowDownIcon } from "@heroicons/react/24/outline";
import { useSelfOrganizationUser } from "../../hooks/useOrganization";
import {
  useEditModeReadOnlyGuard,
  useIsEditModeReadOnly,
} from "../../edit-mode/useIsEditModeReadOnly";

const EmptyInboxView = () => (
  <div className="w-100 text-center py-xl">
    <img src="/assets/images/stay_compliant/empty_inbox.svg" className="mb-lg" />

    <Typography variant="h6">Nice work! You have no open requests.</Typography>
    <Typography variant="subtitle1" className="text-muted">
      Nothing left to do but party
    </Typography>
  </div>
);

const FILTER_STORAGE_TTL_MS = 24 * 60 * 60 * 1000;

type StoredRequestFilters = {
  filters: unknown;
  savedAt: number;
};

const sanitizeFilters = (value: unknown): Filters<any> => {
  if (!Array.isArray(value)) {
    return [];
  }

  return value
    .filter(
      (item) =>
        item && typeof item.id === "string" && Object.prototype.hasOwnProperty.call(item, "value"),
    )
    .map(({ id, value: filterValue }) => ({ id, value: filterValue }));
};

const parseStoredFilters = (
  value: string,
): {
  filters: Filters<any>;
  hasExpired: boolean;
  normalizedFilters: string;
} => {
  const expiredResult = {
    filters: [],
    hasExpired: true,
    normalizedFilters: "[]",
  };

  try {
    const parsedRecord = JSON.parse(value) as StoredRequestFilters;
    const hasValidSavedAt = typeof parsedRecord?.savedAt === "number";
    const hasExpired =
      !hasValidSavedAt || Date.now() - parsedRecord.savedAt >= FILTER_STORAGE_TTL_MS;

    if (hasExpired) {
      return expiredResult;
    }

    const filters = sanitizeFilters(parsedRecord.filters);
    return {
      filters,
      hasExpired: false,
      normalizedFilters: JSON.stringify(filters),
    };
  } catch {
    return expiredResult;
  }
};

const serializeStoredFilters = (filters: Filters<any>) =>
  JSON.stringify({
    filters,
    savedAt: Date.now(),
  });

export function Listing({
  filter,
}: RouteComponentProps & {
  filter: DataRequestFilter;
  show?: DataRequestShowType;
}) {
  // eslint-disable-next-line no-unused-vars, unused-imports/no-unused-vars
  const [seenInboxWelcomeModal, setSeenInboxWelcomeModal] = useLocalStorage(
    "has_seen_inbox_welcome_modal",
    "false",
  );

  const [showScheduleTrainingModal, setShowScheduleTrainingModal] =
    useQueryModal("finished_get_compliant");
  const requestFilterStorageKey = `request-inbox-filters-${filter}`;

  const [everRendered, setEverRendered] = useState(false);
  const primaryOrgId = usePrimaryOrganizationId();

  const [org, ,] = useOrganization(primaryOrgId);
  const showPrivacyEmails = canUsePrivacyEmailInbox(org);
  const { authorizations, request: authorizationsRequest } = useAuthorizations(org?.id);

  const initialSort: Array<SortingRule<string>> = useMemo(() => {
    const sort: Array<SortingRule<string>> = [];
    if (filter === "active") {
      sort.push({ id: "dueIn", desc: false });
    } else if (filter === "complete") {
      sort.push({ id: "closedAt", desc: true });
    } else if (filter === "pending") {
      sort.push({ id: "submitted", desc: true });
    }
    return sort;
  }, [filter]);
  const initialFilters = useMemo(() => {
    const parsedStoredFilters = parseStoredFilters(
      localStorage.getItem(requestFilterStorageKey) || "",
    );
    if (parsedStoredFilters.hasExpired) {
      localStorage.setItem(requestFilterStorageKey, serializeStoredFilters([]));
      return [];
    }

    return parsedStoredFilters.filters;
  }, [requestFilterStorageKey]);

  const {
    state: paginationState,
    updatePage: onPageChange,
    updateSearch: onSearchChange,
    updateSort: onSortChange,
    updateFilter: onFilterChange,
  } = useStatefulPagination({
    sort: initialSort,
    filter: initialFilters,
  });

  useEffect(() => {
    const nextFilters = paginationState.filter ?? [];
    const normalizedNextFilters = JSON.stringify(nextFilters);
    const parsedStoredFilters = parseStoredFilters(
      localStorage.getItem(requestFilterStorageKey) || "",
    );
    if (parsedStoredFilters.normalizedFilters !== normalizedNextFilters) {
      localStorage.setItem(requestFilterStorageKey, serializeStoredFilters(nextFilters));
    }
  }, [paginationState.filter, requestFilterStorageKey]);

  const [dataRequestPage, requestsRequest, fetch] = useRequestSummaries(
    primaryOrgId,
    filter as DataRequestFilter,
    paginationState.filter,
    paginationState.sort,
    paginationState.search,
    "all",
    Math.max(paginationState.page - 1, 0),
  );

  const [dataRequestAllForFilter, requestsAllRequest, fetchAll] = useRequestSummaries(
    primaryOrgId,
    filter as DataRequestFilter,
    paginationState.filter,
    paginationState.sort,
    paginationState.search,
    "all",
    0,
    10000 /* 10,000 requests seems like a good upper-limit for a select-all... */,
  );

  const currentOrgUser = useSelfOrganizationUser();
  const isHrUser = currentOrgUser?.role === "HR";

  const visibleRequestRows = isHrUser
    ? (dataRequestPage?.data ?? []).filter((row) => row.context === "EMPLOYEE")
    : dataRequestPage?.data ?? [];
  const selectionExcluder = useCallback(
    (row: DataSubjectRequestSummaryDto) => !!row?.batchPublicId,
    [],
  );
  const getVisibleRows = useCallback(
    () =>
      visibleRequestRows.map((row) => ({
        locator: row.id,
        item: row,
      })),
    [visibleRequestRows],
  );
  const rowSelectionManager = useRowSelectionManager<DataSubjectRequestSummaryDto>({
    selectionExcluder,
    getVisibleRows,
  });
  const [dataRequestBatches, requestBatchesRequest, fetchBatches] = useRequestBatches(primaryOrgId);
  const isEditModeReadOnly = useIsEditModeReadOnly();
  const guardReadOnlyMode = useEditModeReadOnlyGuard();

  const loading = useMemo(
    () =>
      !NetworkRequest.areFinished(
        requestsRequest,
        requestsAllRequest,
        requestBatchesRequest,
        authorizationsRequest,
      ),
    [requestsRequest, requestsAllRequest, requestBatchesRequest, authorizationsRequest],
  );
  const [newRequestDialogOpen, setNewRequestDialogOpen] = useState(false);

  // ever rendered flag
  useEffect(() => {
    if (
      NetworkRequest.areFinished(
        requestsRequest,
        requestsAllRequest,
        requestBatchesRequest,
        authorizationsRequest,
      )
    ) {
      setEverRendered(true);
    }
  }, [requestsRequest, requestsAllRequest, requestBatchesRequest, authorizationsRequest]);

  const [openRequestsSummary] = useRequestSummaries(
    primaryOrgId,
    "active",
    undefined,
    undefined,
    undefined,
    "all",
    0,
    1,
  );
  const openRequestsCount = openRequestsSummary?.meta?.totalCount;
  const privacyEmailCountParams = useMemo(() => ({ page: 0, per: 1, status: "OPEN" }), []);
  const { result: privacyEmailSummary } = usePrivacyEmailConversations(
    primaryOrgId,
    privacyEmailCountParams,
    showPrivacyEmails,
  );
  const privacyEmailsCount = privacyEmailSummary?.meta?.totalCount;

  const snackbar = useSnackBar();
  const orgId = usePrimaryOrganizationId();
  const showBatches =
    filter === "active" &&
    dataRequestBatches?.dataSubjectRequestBatches.some((batch) => !batch?.closedAt);
  const handleDeleteBatch = async (batchId: string) => {
    if (guardReadOnlyMode()) {
      return;
    }

    const response = await axios.post(ApiRouteHelpers.organizationRequestBatchDelete(orgId), {
      batchPublicId: batchId,
    });

    fetch();
    fetchAll();
    fetchBatches();

    if (response?.data?.success) {
      snackbar(`Ungroup successful`, { variant: "success" });
    } else {
      snackbar(`Couldn't ungroup ${batchId}, try again later`, {
        variant: "error",
      });
    }
  };

  const handleBatchCreatedReloadData = () => {
    fetch();
    fetchAll();
    fetchBatches();
  };

  const csvDownloader = useRequestCSVDownloader({ orgId });

  if (!everRendered) {
    return <LoadingPageWrapper />;
  }

  let isNewRequestButtonDisabled = rowSelectionManager?.selectedRowCount !== 0;
  let newRequestButton = (
    <AquiloButton
      size="s"
      color="primary"
      onClick={() => {
        if (guardReadOnlyMode()) {
          return;
        }

        setNewRequestDialogOpen(true);
      }}
      disabled={isNewRequestButtonDisabled}
    >
      <PlusIcon className="w-[14px] h-[14px] mr-[8px]" /> New Request
    </AquiloButton>
  );

  return (
    <StandardPageWrapper fullWidth>
      <PageHeader titleContent="Request Inbox" />

      <div style={{ maxWidth: "1214px", paddingRight: "32px" }}>
        <div className="tw-boundary">
          <div className="flex justify-between items-end mb-6">
            <RequestInboxTabs
              selectedTabKey={filter as DataRequestFilter}
              counts={{ open: openRequestsCount, privacyEmails: privacyEmailsCount }}
              showPrivacyEmails={showPrivacyEmails}
            />

            {/* See POL-6927 to track allowing creating new requests for HR users */}
            {!isHrUser &&
              (isNewRequestButtonDisabled ? (
                <Tooltip title="Clear the current selection before creating a new request.">
                  {/* Wrapper <div> is necessary for the Tooltip to work */}
                  <div>{newRequestButton}</div>
                </Tooltip>
              ) : (
                newRequestButton
              ))}
          </div>
        </div>

        {/* TODO: un-hide this modal when tutorial videos are added (POL-5802) */}
        {/* {seenInboxWelcomeModal === "false" && (
          <InboxWelcomeModal setSeenInboxWelcomeModal={setSeenInboxWelcomeModal} />
        )} */}

        <RequestsTable
          columnSet={filter as DataRequestFilter}
          data={dataRequestPage?.data}
          allDataForCurrentFilter={dataRequestAllForFilter?.data}
          loading={loading}
          filterPanelOpen={true}
          pageMeta={dataRequestPage?.meta}
          onPageChange={onPageChange}
          onSortChange={onSortChange}
          onFiltersChange={onFilterChange}
          onGlobalSearchChange={onSearchChange}
          initialPageIndex={paginationState.page - 1}
          initialSortBy={paginationState.sort}
          initialFilters={paginationState.filter as Filters<any>}
          initialGlobalFilter={paginationState.search}
          allowRowSelection
          rowSelectionManager={rowSelectionManager}
          onlyShowHrRequests={isHrUser}
          emptyState={filter === "active" ? <EmptyInboxView /> : undefined}
          reloadDataHandler={handleBatchCreatedReloadData}
          batchesTable={
            showBatches && (
              <BatchesTable
                dataSubjectRequestBatches={dataRequestBatches?.dataSubjectRequestBatches}
                onDeleteBatch={handleDeleteBatch}
                isEditModeReadOnly={isEditModeReadOnly}
                onDownloadCsv={(batchId: string) => {
                  void csvDownloader.downloadRequests(batchId);
                }}
              />
            )
          }
        />
      </div>

      <NewRequestDialog
        open={newRequestDialogOpen}
        onClose={() => {
          setNewRequestDialogOpen(false);
          fetch();
          fetchAll();
        }}
      />

      <ScheduleTrainingDialog
        open={showScheduleTrainingModal}
        onClose={() => setShowScheduleTrainingModal(false)}
      />

      {authorizationsRequest.success && !!org.gaWebProperty && (
        <ReconnectGANudgeDialog
          hasGAWebProperty={!!org.gaWebProperty}
          hasGAAuthorization={authorizations.hasGoogle}
          organizationId={orgId}
        />
      )}
    </StandardPageWrapper>
  );
}

function BatchesTable({
  dataSubjectRequestBatches,
  onDeleteBatch,
  onDownloadCsv,
  isEditModeReadOnly,
}: {
  dataSubjectRequestBatches: OrganizationDataSubjectRequestBatchDto[];
  onDeleteBatch: (batchId: string) => void;
  onDownloadCsv: (batchId: string) => void;
  isEditModeReadOnly: boolean;
}) {
  const history = useHistory();

  return (
    <>
      <h3 className="text-h4 text-color-primary-dark">Batches</h3>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>ID</TableCell>
            <TableCell>Size</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>Step</TableCell>
            <TableCell>Earliest Due</TableCell>
            <TableCell className="text-right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {dataSubjectRequestBatches.map((batch: OrganizationDataSubjectRequestBatchDto) => {
            if (batch.closedAt) {
              return null;
            }

            return (
              <TableRow key={batch.id}>
                <TableCell style={{ maxWidth: "100px" }}>
                  <Typography variant="subtitle2">{batch.id}</Typography>
                </TableCell>
                <TableCell style={{ maxWidth: "120px" }}>
                  <Typography variant="body2">{batch.requestCount} requests</Typography>
                </TableCell>
                <TableCell style={{ maxWidth: "80px" }}>
                  <Typography variant="body2">{batch.requestRegulation?.shortLabel}</Typography>
                </TableCell>
                <TableCell style={{ maxWidth: "160px" }}>
                  <Typography variant="body2">{fmtLongRequestStep(batch.requestState)}</Typography>
                </TableCell>
                <TableCell className="pl-0 pr-0">
                  <DueDate date={batch.requestEarliestDueDate} noDueLabel asChip />
                </TableCell>
                <TableCell className="text-right pl-0">
                  <div className="tw-boundary">
                    <div className="flex justify-end gap-[8px]">
                      <AquiloButton
                        size="s"
                        color="tertiary"
                        disabled={isEditModeReadOnly || batch.requestState === "AUTOMATING"}
                        onClick={() => onDeleteBatch(batch.id)}
                      >
                        <Clear className="w-[12px] h-[16px] mr-[8px]" /> Ungroup
                      </AquiloButton>
                      <AquiloButton
                        size="s"
                        color="secondary"
                        onClick={() => onDownloadCsv(batch.id)}
                      >
                        <DocumentArrowDownIcon className="w-[12px] h-[16px] mr-[8px]" /> CSV
                      </AquiloButton>
                      <AquiloButton
                        size="s"
                        color="primary"
                        disabled={batch.requestState === "AUTOMATING"}
                        onClick={() =>
                          history.push(
                            RouteHelpers.requests.detail(batch.representativeRequestPublicId),
                          )
                        }
                      >
                        Process
                      </AquiloButton>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </>
  );
}

export function RequestInboxTabs({
  selectedTabKey,
  counts,
  showPrivacyEmails: showPrivacyEmailsProp,
}: {
  selectedTabKey:
    | DataRequestFilter
    | "tutorials"
    | "record-lookup"
    | "email-settings"
    | "privacy-emails";
  counts?: {
    open?: number;
    privacyEmails?: number;
  };
  showPrivacyEmails?: boolean;
}) {
  const history = useHistory();
  const primaryOrgId = usePrimaryOrganizationId();
  const [org] = useOrganization(primaryOrgId, true);
  const showPrivacyEmails = showPrivacyEmailsProp ?? canUsePrivacyEmailInbox(org);
  const formatLabel = (base: string, count?: number) =>
    typeof count === "number" ? `${base} (${count})` : base;

  const currentOrgUser = useSelfOrganizationUser();
  const isHrUser = currentOrgUser?.role === "HR";

  const tabs = [
    {
      name: formatLabel("Open Requests", counts?.open),
      current: selectedTabKey === "active",
      onClick: () => {
        history.push(Routes.requests.active);
      },
    },
    ...(showPrivacyEmails
      ? [
          {
            name: formatLabel("Privacy Emails", counts?.privacyEmails),
            current: selectedTabKey === "privacy-emails",
            onClick: () => {
              history.push(Routes.requests.privacyEmails);
            },
          },
        ]
      : []),
    {
      name: "Closed Requests",
      current: selectedTabKey === "complete",
      onClick: () => {
        history.push(Routes.requests.closed);
      },
    },
    {
      name: AUTOMATING_TAB_LABEL,
      current: selectedTabKey === "pending",
      onClick: () => {
        history.push(Routes.requests.pending);
      },
    },
    {
      name: "Record Lookup",
      current: selectedTabKey === "record-lookup",
      onClick: () => {
        history.push(Routes.records.recordLookup);
      },
    },
    ...(!isHrUser
      ? [
          {
            name: "Settings",
            current: selectedTabKey === "email-settings",
            onClick: () => {
              history.push(Routes.requests.privacyEmailSettings);
            },
          },
        ]
      : []),
  ];

  return <TabsWithUnderline rightPadding="pr-[50px]" tabs={tabs} />;
}

// eslint-disable-next-line unused-imports/no-unused-vars
function InboxWelcomeModal({
  setSeenInboxWelcomeModal,
}: {
  setSeenInboxWelcomeModal: (value: string) => void;
}) {
  const history = useHistory();

  return (
    <div className="tw-boundary">
      <Modal
        title="Welcome to your Inbox"
        alignText="center"
        firstButton="Continue to Inbox"
        firstButtonColor="tertiary"
        onFirstButtonClick={() => {
          setSeenInboxWelcomeModal("true");
        }}
        secondButton="Watch Tutorials"
        secondButtonColor="primary"
        onSecondButtonClick={() => {
          setSeenInboxWelcomeModal("true");
          history.push(Routes.requests.tutorials);
        }}
        widthButton="full"
        showOverlay
        closeButton
        onClose={() => {
          setSeenInboxWelcomeModal("true");
        }}
      >
        <p className="text-gray-500 text-[14px] text-center">
          All your consumers requests will show up here and will be processed based on the workflows
          you set up.
        </p>
        <img
          className="w-[103px] h-[88px] mt-[40px] mx-auto"
          src="/assets/images/stay_compliant/dashboard/setup-truevault-setup-workflows-step-illustration.svg"
          alt="Inbox"
        />
      </Modal>
    </div>
  );
}
